import { Spinner } from '@/components/ui/Spinner';
import { GOLD_CONSTANTS } from '@/economy/upgrades';
import { BONUS_SYSTEM } from '@/game/constants';
import { useEffect, useState } from 'react';

interface TitleScreenProps {
  ready: boolean;
  pokiMode: string;
  gold: number;
  tokens: number;
  lastTokenAdAt: number;
  lastGoldAdAt: number;
  challengeScore?: number | null;
  tokenCooldownMs?: number;
  goldCooldownMs?: number;
  onStart: () => void;
  onOpenLeaderboard: () => void;
  onOpenSettings: () => void;
  onOpenHowToPlay: () => void;
  onOpenShop: () => void;
  onOpenPrivacy: () => void;
  onOpenTerms: () => void;
  adsEnabled?: boolean;
}

const TOKEN_COOLDOWN_MS = BONUS_SYSTEM.AD_COOLDOWN_MS;
const TOKEN_CAP = BONUS_SYSTEM.MAX_TOKENS;
const GOLD_COOLDOWN_MS = GOLD_CONSTANTS.AD_COOLDOWN_MINUTES * 60 * 1000;

export function TitleScreen({
  ready,
  pokiMode,
  gold,
  tokens,
  lastTokenAdAt,
  lastGoldAdAt,
  challengeScore = null,
  tokenCooldownMs = TOKEN_COOLDOWN_MS,
  goldCooldownMs = GOLD_COOLDOWN_MS,
  onStart,
  onOpenLeaderboard,
  onOpenSettings,
  onOpenHowToPlay,
  onOpenShop,
  onOpenPrivacy,
  onOpenTerms,
  adsEnabled = true,
}: TitleScreenProps) {
  const [now, setNow] = useState(() => Date.now());

  const tokenReady = adsEnabled && tokens < TOKEN_CAP && now - lastTokenAdAt >= tokenCooldownMs;
  const goldReady = adsEnabled && now - lastGoldAdAt >= goldCooldownMs;
  const rewardsReady = tokenReady || goldReady;
  const anyOnCooldown = adsEnabled && (!tokenReady || !goldReady);

  useEffect(() => {
    if (!anyOnCooldown) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [anyOnCooldown]);

  return (
    <div className="title-overlay" aria-label="Slime Pang title screen">
      <section className="title-card" aria-label="Title screen">
        <div className="title-chips" aria-label="Currency">
          <span className="chip chip--gold" aria-label={`${gold} gold`}>
            <span className="coin-icon" aria-hidden="true" />
            <span>{gold}</span>
          </span>
          {adsEnabled && (
            <span className="chip chip--token" aria-label={`${tokens} bonus tokens`}>
              <span aria-hidden="true">⭐</span>
              <span>
                {tokens}/{TOKEN_CAP}
              </span>
            </span>
          )}
        </div>

        {challengeScore !== null && challengeScore > 0 && (
          <output className="title-challenge">
            Beat my score: {challengeScore.toLocaleString()}
          </output>
        )}
        <div className="title-logo-row">
          <span className="title-slime-decor title-slime-decor--blue" aria-hidden="true" />
          <h1 className="title-logo">Slime Pang</h1>
          <span className="title-slime-decor title-slime-decor--red" aria-hidden="true" />
        </div>
        <p className="title-tagline">Pop the slime that matches the side panel.</p>

        <div className="title-actions">
          <button
            type="button"
            className="title-btn title-btn--primary title-start"
            onClick={onStart}
            disabled={!ready}
          >
            {ready ? (
              'Start'
            ) : (
              <>
                <Spinner />
                Loading…
              </>
            )}
          </button>
          <button type="button" className="title-btn" onClick={onOpenLeaderboard} disabled={!ready}>
            Leaderboard
          </button>
          <button
            type="button"
            className="title-btn title-shop"
            onClick={onOpenShop}
            data-rewards-ready={rewardsReady ? 'true' : 'false'}
          >
            Shop
            {rewardsReady && (
              <>
                <span className="rewards-badge" aria-hidden="true">
                  FREE
                </span>
                <span className="visually-hidden"> · free rewards available</span>
              </>
            )}
          </button>
          <button type="button" className="title-btn" onClick={onOpenHowToPlay}>
            How to play
          </button>
          <button type="button" className="title-btn" onClick={onOpenSettings}>
            Settings
          </button>
        </div>

        <div className="title-footer">
          <button type="button" className="title-footer-link" onClick={onOpenPrivacy}>
            Privacy
          </button>
          <span className="title-footer-sep" aria-hidden="true">
            ·
          </span>
          <button type="button" className="title-footer-link" onClick={onOpenTerms}>
            Terms
          </button>
        </div>

        {import.meta.env.DEV && <small className="title-mode">Poki mode: {pokiMode}</small>}
      </section>
    </div>
  );
}
